<template>
  <div class="filter-container">
    <button
      class="filter"
      id="filterButton"
      @click="toggleDropdown"
      :aria-expanded="isDropdownOpen"
      aria-haspopup="menu"
      aria-label="Open filter options"
      aria-describedby="filter-description"
    >
      <svg viewBox="0 0 24 24" height="1em" aria-hidden="true">
        <rect x="2" y="2" width="20" height="4" class="filter-line top-line"/>
        <rect x="2" y="10" width="20" height="4" class="filter-line middle-line"/>
        <rect x="2" y="18" width="20" height="4" class="filter-line bottom-line"/>
      </svg>
      <span id="filter-description" class="visually-hidden">Filter and sort search results</span>
    </button>

    <div
      class="dropdown-menu"
      :class="{ show: isDropdownOpen }"
      id="dropdownMenu"
      role="menu"
      :aria-hidden="!isDropdownOpen"
      aria-labelledby="filterButton"
      @click.stop
    >
      <section class="dropdown-section" role="group" aria-labelledby="filter-by-heading">
        <h3 id="filter-by-heading" class="section-title">Filter by</h3>

        <fieldset class="slider-container" role="radiogroup" aria-labelledby="show-legend">
          <legend id="show-legend" class="slider-indicator">Show...</legend>

          <!-- Visual slider track (decorative) -->
          <div class="slider-track" @click="handleSliderClick" role="presentation" aria-hidden="true">
            <div class="slider-thumb" :style="{ left: sliderThumbPosition }" aria-hidden="true"></div>
          </div>

          <!-- Accessible radio button group -->
          <div class="slider-labels" role="group" aria-label="User type filter options">
            <label class="slider-label" :class="{ active: currentSliderFilter === 'artists' }">
              <input
                type="radio"
                name="user-type-filter"
                value="artists"
                :checked="currentSliderFilter === 'artists'"
                @change="updateSlider('artists')"
                class="visually-hidden"
                aria-describedby="artists-description"
              />
              <span class="radio-label-text">Artists</span>
              <span id="artists-description" class="visually-hidden">Show only artists</span>
            </label>

            <label class="slider-label" :class="{ active: currentSliderFilter === 'both' }">
              <input
                type="radio"
                name="user-type-filter"
                value="both"
                :checked="currentSliderFilter === 'both'"
                @change="updateSlider('both')"
                class="visually-hidden"
                aria-describedby="both-description"
              />
              <span class="radio-label-text">Both</span>
              <span id="both-description" class="visually-hidden">Show both artists and scientists</span>
            </label>

            <label class="slider-label" :class="{ active: currentSliderFilter === 'scientists' }">
              <input
                type="radio"
                name="user-type-filter"
                value="scientists"
                :checked="currentSliderFilter === 'scientists'"
                @change="updateSlider('scientists')"
                class="visually-hidden"
                aria-describedby="scientists-description"
              />
              <span class="radio-label-text">Scientists</span>
              <span id="scientists-description" class="visually-hidden">Show only scientists</span>
            </label>
          </div>
        </fieldset>

        <div class="checkbox-option" @click="filters.collaboration = !filters.collaboration; emitFilters()">
          <label for="collaboration-filter" class="option-text">Seeking collaborations</label>
          <div class="content">
            <div class="checkBox">
              <input
                id="collaboration-filter"
                name="filter_collaboration"
                type="checkbox"
                v-model="filters.collaboration"
                @change="emitFilters"
                aria-describedby="collaboration-description"
              />
              <div class="transition"></div>
            </div>
          </div>
          <span id="collaboration-description" class="visually-hidden">Filter to show only users seeking collaborations</span>
        </div>
      </section>

      <section class="dropdown-section" role="group" aria-labelledby="tags-heading">
        <h3 id="tags-heading" class="section-title">Tags</h3>
        <div class="tags-container">
          <div class="tags-list" id="tagsList" role="list" aria-label="Selected tags">
            <button
              v-for="tag in tags"
              :key="tag"
              class="tag"
              :data-theme="getTagTheme(tag)"
              @click="removeTag(tag)"
              :aria-label="`Remove ${tag} tag`"
              role="listitem"
            >
              <span class="tag-action" aria-hidden="true">−</span>
              <span class="tag-text">{{ tag }}</span>
            </button>
          </div>

          <div class="tag-input-wrapper" @click.stop>
            <label for="tagInput" class="visually-hidden">Search for tags</label>
            <input
              type="text"
              id="tagInput"
              class="tag-input"
              placeholder="Search tags..."
              v-model="tagSearchQuery"
              @click.stop
              @keypress.enter.prevent="addTagFromInput"
              aria-describedby="tag-input-help"
              autocomplete="off"
            />
            <span id="tag-input-help" class="visually-hidden">Type a tag name and press Enter to add, or select from suggestions below</span>
          </div>

          <div class="tag-suggestions" id="tagSuggestions" role="region" :aria-label="suggestionsLabelText || 'Available tags'">
            <div v-if="suggestionsLabelText" class="suggestions-label" aria-hidden="true">{{ suggestionsLabelText }}</div>
            <div class="suggestions-pills" role="list" aria-label="Available tag suggestions">
              <button
                v-for="suggestion in filteredSuggestions"
                :key="suggestion.name"
                class="suggestion-tag-pill"
                :data-tag="suggestion.name"
                :data-theme="suggestion.theme"
                @click.stop="toggleTag(suggestion.name)"
                :aria-label="tags.includes(suggestion.name) ? `Remove ${suggestion.name} tag` : `Add ${suggestion.name} tag`"
                role="listitem"
              >
                <span class="tag-action" aria-hidden="true">{{ tags.includes(suggestion.name) ? '−' : '+' }}</span>
                <span class="tag-text">{{ suggestion.name }}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section class="dropdown-section" role="group" aria-labelledby="sort-by-heading">
        <h3 id="sort-by-heading" class="section-title">Sort by</h3>
        <fieldset role="radiogroup" aria-labelledby="sort-by-heading">
          <legend class="visually-hidden">Sort options</legend>

          <label class="dropdown-option" :class="{ selected: currentSort === 'relevance' }">
            <input
              type="radio"
              name="sort-option"
              value="relevance"
              :checked="currentSort === 'relevance'"
              @change="selectSortOption('relevance')"
              class="visually-hidden"
            />
            <span class="option-text">Relevance</span>
          </label>

          <label class="dropdown-option" :class="{ selected: currentSort === 'date-joined' }">
            <input
              type="radio"
              name="sort-option"
              value="date-joined"
              :checked="currentSort === 'date-joined'"
              @change="selectSortOption('date-joined')"
              class="visually-hidden"
            />
            <span class="option-text">Date joined</span>
          </label>

          <label class="dropdown-option" :class="{ selected: currentSort === 'latest-activity' }">
            <input
              type="radio"
              name="sort-option"
              value="latest-activity"
              :checked="currentSort === 'latest-activity'"
              @change="selectSortOption('latest-activity')"
              class="visually-hidden"
            />
            <span class="option-text">Latest activity</span>
          </label>
        </fieldset>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue';
import { useTags } from '@/composables/useTags.js';

// Get tags from the composable (Firestore-first with static fallback)
const { tags: availableTags, getTagTheme } = useTags();

const isDropdownOpen = ref(false);
const currentSort = ref('relevance');
const currentSliderFilter = ref('both');
const tagSearchQuery = ref('');
const tags = ref([]);  // Selected tags for filtering

// Removed unused refs - using document.querySelector instead

const filters = reactive({
  collaboration: false,
});

const emit = defineEmits(['update-filters']);

// Logic from your HTML file's <script> block
const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const handleClickOutside = (event) => {
  // Check if the click is outside the entire filter container
  const filterContainer = document.querySelector('.filter-container');
  if (filterContainer && !filterContainer.contains(event.target)) {
    isDropdownOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const emitFilters = () => {
  emit('update-filters', {
    sort: currentSort.value,
    tags: tags.value,
    type: currentSliderFilter.value,
    collaboration: filters.collaboration,
  });
};

const sliderThumbPosition = computed(() => {
  const positions = {
    'artists': 16.67,
    'both': 50,
    'scientists': 83.33,
  };
  return positions[currentSliderFilter.value] + '%';
});

const updateSlider = (filterType) => {
  currentSliderFilter.value = filterType;
  emitFilters();
};

const handleSliderClick = (e) => {
  const sliderTrack = e.currentTarget;
  const rect = sliderTrack.getBoundingClientRect();
  const clickX = e.clientX - rect.left;
  const percentage = (clickX / rect.width) * 100;
  
  if (percentage < 33.33) {
    updateSlider('artists');
  } else if (percentage < 66.66) {
    updateSlider('both');
  } else {
    updateSlider('scientists');
  }
};

const selectSortOption = (sortType) => {
  currentSort.value = sortType;
  emitFilters();
};

// Use dynamic tags from composable (Firestore-backed)
const filteredSuggestions = computed(() => {
  // Get selected tag names in lowercase for comparison
  const selectedLower = tags.value.map(t => t.toLowerCase());

  let filtered;
  if (!tagSearchQuery.value) {
    filtered = availableTags.value.filter(s => !selectedLower.includes(s.name.toLowerCase()));
  } else {
    filtered = availableTags.value.filter(s =>
      s.name.toLowerCase().includes(tagSearchQuery.value.toLowerCase()) && !selectedLower.includes(s.name.toLowerCase())
    );
  }

  // Sort alphabetically by name
  return filtered.sort((a, b) => a.name.localeCompare(b.name));
});

const suggestionsLabelText = computed(() => {
  return tagSearchQuery.value ? 'Search Results' : '';
});

// Use imported getTagTheme function

// Helper to convert to Title Case
const toTitleCase = (str) => {
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

const addTag = (tagName) => {
  const trimmedTag = tagName.trim();
  // Find the tag in available tags to get proper casing, or convert to Title Case
  const matchedTag = availableTags.value.find(s => s.name.toLowerCase() === trimmedTag.toLowerCase());
  const finalTag = matchedTag ? matchedTag.name : toTitleCase(trimmedTag);

  // Check if already added (case-insensitive)
  const alreadyAdded = tags.value.some(t => t.toLowerCase() === finalTag.toLowerCase());
  if (finalTag && !alreadyAdded) {
    tags.value.push(finalTag);
    emitFilters();
  }
};

const removeTag = (tagName) => {
  tags.value = tags.value.filter(t => t.toLowerCase() !== tagName.toLowerCase());
  emitFilters();
};

const addTagFromInput = () => {
  if (tagSearchQuery.value) {
    addTag(tagSearchQuery.value);
    tagSearchQuery.value = '';
  }
};

const toggleTag = (tagName) => {
  if (tags.value.includes(tagName)) {
    removeTag(tagName);
  } else {
    addTag(tagName);
  }
};

// Clear all filters - exposed to parent component
const clearAllFilters = () => {
  tags.value = [];
  tagSearchQuery.value = '';
  currentSliderFilter.value = 'both';
  filters.collaboration = false;
  emitFilters();
};

// Expose method for parent to call
defineExpose({
  clearAllFilters
});

watch(tags, () => {
  emitFilters();
}, { deep: true });

</script>

<style scoped>
/* ===== ACCESSIBILITY STYLES ===== */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

/* Enhanced focus indicators for keyboard users */
.filter:focus:not(:focus-visible),
.dropdown-option:focus:not(:focus-visible),
.slider-label:focus:not(:focus-visible),
.tag:focus:not(:focus-visible),
.suggestion-tag-pill:focus:not(:focus-visible),
.tag-input:focus:not(:focus-visible),
.checkBox:focus:not(:focus-visible) {
  outline: none;
}

.filter:focus-visible,
.dropdown-option:focus-visible,
.tag:focus-visible,
.suggestion-tag-pill:focus-visible,
.tag-input:focus-visible {
  outline: 2px solid #6566be;
  outline-offset: 2px;
}

.slider-label:focus-within,
.dropdown-option:focus-within {
  outline: 2px solid #6566be;
  outline-offset: 2px;
  border-radius: 4px;
}

.checkBox:focus-within {
  outline: 2px solid #6566be;
  outline-offset: 2px;
  border-radius: 50%;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .filter,
  .dropdown-option,
  .slider-label,
  .tag,
  .suggestion-tag-pill,
  .tag-input {
    border-width: 2px;
  }

  .dropdown-option.selected {
    background-color: currentColor;
    color: white;
  }

  .slider-label.active {
    background-color: currentColor;
    color: white;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .filter,
  .dropdown-option,
  .slider-label,
  .tag,
  .suggestion-tag-pill,
  .dropdown-menu,
  .slider-thumb {
    transition: none;
    animation: none;
  }
}

/* Touch target size for mobile */
@media (max-width: 768px) {
  .filter,
  .dropdown-option,
  .slider-label,
  .tag,
  .suggestion-tag-pill {
    min-height: 44px;
    min-width: 44px;
    padding: 12px;
  }

  .tag-input {
    min-height: 44px;
    padding: 12px;
  }

  .filter {
    width: 60px;
    height: 60px;
  }
}

/* Fieldset and legend styling */
fieldset {
  border: none;
  padding: 0;
  margin: 0;
}

legend {
  padding: 0;
  margin-bottom: 8px;
}

.radio-label-text {
  pointer-events: none;
}

.option-text {
  pointer-events: none;
}

        .filter-container {
            position: relative;
            display: inline-block;
        }

        /* Filter button styling */
        .filter {
            /* Existing styles for the button's default state */
            width: 50px;
            height: 50px;
            border-radius: var(--stdRadius);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s;
            position: relative;
            border: none;
            background: transparent;
        }

        .filter  svg {
            height: 20px;
        }

        .filter .top-line {
            fill: var(--artColor);
        }

        .filter .middle-line {
            fill: var(--blendColor);
        }

        .filter .bottom-line {
            fill: var(--sciColor);
        }



        .filter:hover {
            scale: 1.3;
                }

        /* Custom checkbox styling */
        .checkbox-wrapper * {
            -webkit-tap-highlight-color: transparent;
            outline: none;
        }
        
        .checkbox-wrapper input[type="checkbox"] {
            display: none;
        }
        
        .checkbox-wrapper label {
            --size: 20px;
            position: relative;
            display: block;
            width: var(--size);
            height: var(--size);
            margin: 0;
            background-color: #2563eb;
            border-radius: 50%;
            cursor: pointer;
            transition: 0.2s ease transform, 0.2s ease background-color;
            overflow: hidden;
            z-index: 1;
        }
        
        .checkbox-wrapper label:before {
            content: "";
            position: absolute;
            top: 50%;
            right: 0;
            left: 0;
            width: calc(var(--size) * 0.7);
            height: calc(var(--size) * 0.7);
            margin: 0 auto;
            background-color: #fff;
            transform: translateY(-50%);
            border-radius: 50%;
            transition: 0.2s ease width, 0.2s ease height;
        }
        
        .checkbox-wrapper label:hover:before {
            width: calc(var(--size) * 0.7);
            height: calc(var(--size) * 0.7);
        }
        
        .checkbox-wrapper label:active {
            transform: scale(0.9);
        }
        
        .checkbox-wrapper .tick_mark {
            position: absolute;
            top: 3px;
            left: 1px;
            right: 0;
            width: calc(var(--size) * 0.6);
            height: calc(var(--size) * 0.6);
            margin: 0 auto;
            margin-left: calc(var(--size) * 0.14);
            transform: rotateZ(-92deg);
        }
        
        .checkbox-wrapper .tick_mark:before,
        .checkbox-wrapper .tick_mark:after {
            content: "";
            position: absolute;
            background-color: #fff;
            border-radius: 2px;
            opacity: 0;
            transition: 0.2s ease transform, 0.2s ease opacity;
        }
        
        .checkbox-wrapper .tick_mark:before {
            left: 0;
            bottom: 0;
            width: calc(var(--size) * 0.1);
            height: calc(var(--size) * 0.3);
            transform: translateY(calc(var(--size) * -0.68));
        }
        
        .checkbox-wrapper .tick_mark:after {
            left: 0;
            bottom: 0;
            width: 100%;
            height: calc(var(--size) * 0.1);
            transform: translateX(calc(var(--size) * 0.78));
        }
        
        .checkbox-wrapper input[type="checkbox"]:checked + label {
            background-color: #dc2626;
        }
        
        .checkbox-wrapper input[type="checkbox"]:checked + label:before {
            width: 0;
            height: 0;
        }
        
        .checkbox-wrapper input[type="checkbox"]:checked + label .tick_mark:before,
        .checkbox-wrapper input[type="checkbox"]:checked + label .tick_mark:after {
            background-color: #fff;
            width: calc(var(--size) * 0.4);
            height: calc(var(--size) * 0.1);
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            opacity: 1;
        }
        
        .checkbox-wrapper input[type="checkbox"]:checked + label .tick_mark:before {
            transform: translate(-50%, -50%) rotate(45deg);
        }
        
        .checkbox-wrapper input[type="checkbox"]:checked + label .tick_mark:after {
            transform: translate(-50%, -50%) rotate(-45deg);
        }

        /* Slider styles */
        .slider-container {
            padding: var(--paddingStandard);
            margin-bottom: 4px;
        }

        .slider-track {
            position: relative;
            height: 40px;
            background: linear-gradient(to right, var(--artColor) 0%, var(--blendColor) 50%, var(--sciColor) 100%);
            border-radius: var(--stdRadius);
            margin-bottom: 12px;
            cursor: pointer;
        }

        .slider-thumb {
            position: absolute;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 36px;
            height: 36px;
            background: var(--bckColor);
            border-radius: 50%;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
            transition: left 0.3s ease;
            left: 16.67%;
        }

        .slider-indicator {
            font-size: 10px;
            color: var(--textColor);
            opacity: 0.5;
            margin-bottom: 4px;
            padding: 0 8px;
            text-align: center;
        }

        .slider-labels {
            display: flex;
            justify-content: space-between;
            padding: 0 8px;
        }

        .slider-label {
            font-size: 12px;
            color: var(--textColor);
            opacity: 0.6;
            transition: opacity 0.3s ease;
            cursor: pointer;
            text-align: center;
            flex: 1;
        }

        .slider-label.active {
            opacity: 1;
            font-weight: bold;
        }

        /* Premium checkbox styling */
        .content {
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .checkBox {
            display: block;
            cursor: pointer;
            width: 20px;
            height: 20px;
            border: 2px solid rgba(255, 255, 255, 0);
            border-radius: var(--stdRadius);
            position: relative;
            overflow: hidden;
            box-shadow: 0px 0px 0px 2px var(--blendColor);
        }

        .checkBox div {
            width: 40px;
            height: 40px;
            background-color: var(--blendColor);
            top: -34px;
            left: -34px;
            position: absolute;
            transform: rotateZ(45deg);
            z-index: 100;
        }

        .checkBox input[type=checkbox]:checked + div {
            left: -6px;
            top: -6px;
        }

        .checkBox input[type=checkbox] {
            position: absolute;
            left: 50px;
            visibility: hidden;
        }

        .transition {
            transition: 300ms ease;
        }

        /* Checkbox option layout */
        .checkbox-option {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 4px 16px;
            cursor: pointer;
            transition: background-color 0.2s ease;
        }

        .checkbox-option:hover {
            background-color: transparent;
        }

        .option-text {
            margin: 0;
            font-size: 14px;
            font-family: var(--font-primary);
            color: var(--textColor);
        }

        /* Tag system styling */
        .tags-container {
            padding: 0 var(--paddingStandard);
        }

        .tag-input-wrapper {
            margin-bottom: 0px;
            background-color: var(--bckColor);
            border: none;
            border-radius: var(--stdRadius) var(--stdRadius) 0 0;
            padding: 0;
        }

        .tag-input {
            width: 100%;
            background-color: transparent;
            color: var(--textColor);
            padding: .15rem .5rem;
            min-height: 30px;
            border-radius: 0;
            outline: none;
            border: none;
            line-height: 1.15;
            font-size: 14px;
            font-family: var(--font-primary);
            transition: all 0.2s ease;
            box-sizing: border-box;
        }

        .tag-input::placeholder {
            color: var(--textColor);
            font-size: 14px;
        }


        /* Tag suggestions as container */
        .tag-suggestions {
            background: var(--bckColor);
            border: none;
            border-radius: 0 0 var(--stdRadius) var(--stdRadius);
            padding: 8px 12px;
            margin-top: 8px;
            max-width: 100%;
            max-height: 150px;
            overflow-y: auto;
            position: relative;
        }

        .tag-suggestions::-webkit-scrollbar {
            width: 6px;
        }

        .tag-suggestions::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
        }

        .tag-suggestions::-webkit-scrollbar-thumb {
            background: var(--textColor);
            border-radius: 3px;
        }

        .tag-suggestions::-webkit-scrollbar-thumb:hover {
            background: var(--artColor);
        }

        .suggestions-label {
            font-size: 11px;
            color: var(--textColor);
            margin-bottom: 6px;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            opacity: 0.8;
        }

        .suggestions-pills {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
        }

        .suggestion-tag-pill {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 3px 8px;
            background: var(--neutralColor);
            border: var(--stdOutline) solid var(--borderColor);
            border-radius: 4px;
            font-size: 10px;
            font-family: var(--font-primary);
            color: var(--textColor);
            cursor: pointer;
            transition: all 0.2s ease;
            white-space: nowrap;
        }

        .suggestion-tag-pill[data-theme="art"] {
            background: var(--bckColor);
            border: var(--stdOutline) solid var(--artOutlineColor);
            color: var(--textColor);
        }

        .suggestion-tag-pill[data-theme="art"] .tag-action {
            color: var(--artColor);
        }

        .suggestion-tag-pill[data-theme="art"] .tag-text {
            color: #555;
        }

        .suggestion-tag-pill[data-theme="science"] {
            background: var(--bckColor);
            border: var(--stdOutline) solid var(--sciOutlineColor);
            color: var(--textColor);
        }

        .suggestion-tag-pill[data-theme="science"] .tag-action {
            color: var(--sciColor);
        }

        .suggestion-tag-pill[data-theme="science"] .tag-text {
            color: #555;
        }

        .suggestion-tag-pill[data-theme="neutral"] {
            background: var(--bckColor);
            border: var(--stdOutline) solid var(--borderColor);
            color: var(--textColor);
        }

        .suggestion-tag-pill[data-theme="neutral"] .tag-action {
            color: var(--textColor);
        }

        .suggestion-tag-pill[data-theme="neutral"] .tag-text {
            color: #555;
        }

        .suggestion-tag-pill.added[data-theme="art"] {
            background: var(--neutralColor);
            border: var(--stdOutline) solid var(--borderColor);
            color: var(--artColor);
        }

        .suggestion-tag-pill.added[data-theme="art"] .tag-action {
            color: var(--textColor);
        }

        .suggestion-tag-pill.added[data-theme="science"] {
            background: var(--neutralColor);
            border: var(--stdOutline) solid var(--borderColor);
            color: var(--sciColor);
        }

        .suggestion-tag-pill.added[data-theme="science"] .tag-action {
            color: var(--textColor);
        }

        .suggestion-tag-pill.added[data-theme="neutral"] {
            background: var(--neutralColor);
            border: var(--stdOutline) solid var(--borderColor);
            color: var(--textColor);
        }

        .suggestion-tag-pill.added[data-theme="neutral"] .tag-action {
            color: var(--textColor);
        }

        .tag-action {
            font-weight: bold;
            font-size: 12px;
            color: var(--textColor);
            line-height: 1;
            min-width: 12px;
            text-align: center;
        }

        .tag-text {
            font-size: 10px;
            color: #555;
        }

        .suggestion-tag-pill[data-theme="art"]:hover:not(.added) {
            background: var(--artOutlineColor);
            border-color: var(--artColor);
        }

        .suggestion-tag-pill[data-theme="art"]:hover:not(.added) .tag-action {
            color: var(--artColor);
        }

        .suggestion-tag-pill[data-theme="science"]:hover:not(.added) {
            background: var(--sciOutlineColor);
            border-color: var(--sciColor);
        }

        .suggestion-tag-pill[data-theme="science"]:hover:not(.added) .tag-action {
            color: var(--sciColor);
        }

        .suggestion-tag-pill[data-theme="neutral"]:hover:not(.added) {
            background: var(--borderColor);
            border-color: var(--textColor);
        }

        .suggestion-tag-pill[data-theme="neutral"]:hover:not(.added) .tag-action {
            color: var(--textColor);
        }

        .suggestion-tag-pill.added[data-theme="art"]:hover {
            background: var(--bckColor);
            border-color: var(--artOutlineColor);
        }

        .suggestion-tag-pill.added[data-theme="art"]:hover .tag-action {
            color: var(--artColor);
        }

        .suggestion-tag-pill.added[data-theme="science"]:hover {
            background: var(--bckColor);
            border-color: var(--sciOutlineColor);
        }

        .suggestion-tag-pill.added[data-theme="science"]:hover .tag-action {
            color: var(--sciColor);
        }

        .suggestion-tag-pill.added[data-theme="neutral"]:hover {
            background: var(--bckColor);
            border-color: var(--borderColor);
        }

        .suggestion-tag-pill.added[data-theme="neutral"]:hover .tag-action {
            color: var(--textColor);
        }

        .suggestion-tag-pill:active {
            background: #d1d5db;
        }

        .tags-list {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
            padding: 0 var(--paddingStandard) 8px var(--paddingStandard);
            min-height: 0px;
        }

        .tags-list:empty {
            display: none;
        }

        .tag {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 3px 8px;
            border-radius: 4px;
            font-size: 10px;
            color: var(--textColor);
        }

        .tag[data-theme="art"] {
            background: var(--artOutlineColor);
            border: var(--stdOutline) solid var(--artColor);
            color: var(--artColor);
        }

        .tag[data-theme="art"] .tag-action {
            color: var(--artColor);
        }

        .tag[data-theme="art"] .tag-text {
            color: #555;
        }

        .tag[data-theme="science"] {
            background: var(--sciOutlineColor);
            border: var(--stdOutline) solid var(--sciColor);
            color: var(--sciColor);
        }

        .tag[data-theme="science"] .tag-action {
            color: var(--sciColor);
        }

        .tag[data-theme="science"] .tag-text {
            color: #555;
        }

        .tag[data-theme="neutral"] {
            background: var(--neutralColor);
            border: var(--stdOutline) solid var(--borderColor);
            color: var(--textColor);
        }

        .tag[data-theme="neutral"] .tag-action {
            color: var(--textColor);
        }

        .tag[data-theme="neutral"] .tag-text {
            color: #555;
        }

        .tag[data-theme="art"]:hover {
            background: var(--bckColor);
            border-color: var(--artOutlineColor);
        }

        .tag[data-theme="art"]:hover .tag-action {
            color: var(--artColor);
        }

        .tag[data-theme="science"]:hover {
            background: var(--bckColor);
            border-color: var(--sciOutlineColor);
        }

        .tag[data-theme="science"]:hover .tag-action {
            color: var(--sciColor);
        }

        .tag[data-theme="neutral"]:hover {
            background: var(--bckColor);
            border-color: var(--borderColor);
        }

        .tag[data-theme="neutral"]:hover .tag-action {
            color: var(--textColor);
        }

        .tag .tag-action {
            cursor: pointer;
            font-weight: bold;
            font-size: 12px;
            color: var(--textColor);
            line-height: 1;
            min-width: 14px;
            text-align: center;
            transition: transform 0.2s ease;
        }



        /* Dropdown menu styling */
        .dropdown-menu {
            position: absolute;
            top: 60px;
            right: 0;
            background: var(--bckColor);
            border-radius: var(--stdRadius);
            box-shadow: 0px 20px 40px rgba(0, 0, 0, 0.15);
            border: none;
            width: 280px;
            min-width: 280px;
            max-width: 280px;
            opacity: 0;
            visibility: hidden;
            transform: translateY(-10px);
            transition: all 0.3s ease;
            z-index: 1000;
        }

        .dropdown-menu.show {
            opacity: 1;
            visibility: visible;
            transform: translateY(0);
        }

        .dropdown-section {
            padding: 16px 0;
        }

        .dropdown-section:not(:last-child) {
            border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        }

        .section-title {
            font-size: 13px;
            font-weight: 600;
            font-family: var(--font-primary);
            color: var(--headersColor);
            margin-bottom: 8px;
            padding: 0 16px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .dropdown-option {
            display: flex;
            align-items: center;
            padding: 8px 16px;
            cursor: pointer;
            transition: background-color 0.2s ease;
            font-size: 14px;
            font-family: var(--font-primary);
            color: var(--textColor);
        }

        .dropdown-option:hover {
            background-color: var(--neutralColor);
        }

        .dropdown-option.selected {
            background-color: var(--neutralColor);
            color: var(--bckColor);
        }

        .option-icon {
            width: 16px;
            height: 16px;
            margin-right: 12px;
            border-radius: 3px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .free-icon { background-color: #22c55e; }
        .request-icon { background-color: #f59e0b; }
        .invite-icon { background-color: #ef4444; }

        .option-dot {
            width: 6px;
            height: 6px;
            background: white;
            border-radius: 50%;
        }

        /* Test area styling */
        .test-area {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 30px;
        }

        .demo-title {
            font-size: var(--titleSubText);
            font-weight: var(--font-weight-bold);
            font-family: var(--font-primary);
            color: var(--headersColor);
        }

        .current-filters {
            padding: var(--paddingSpaceous);
            background: var(--bckColor);
            border-radius: var(--stdRadius);
            box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.1);
            min-width: 300px;
            text-align: center;
        }

        .filter-display {
            font-size: 14px;
            font-family: var(--font-primary);
            color: var(--textColor);
            margin-top: var(--paddingStandard);
        }
</style>
